/**
 * On-Call Swaps API Services
 */

import datacentral from '@/utils/axios/datacentral'
import { ErrorResponse, ResponseArray, ResponseObject } from '@/types/shared'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'

import { OncallSwapResponse } from '../types/response'

import {
    CreateSwapPayload,
    SwapFeedbackPayload,
    SwapFilters,
    MySwapsFilters,
} from '../types/payload'

export const getOncallSwaps = async (
    payload?: DataGridPayload & SwapFilters
) => {
    const params = new URLSearchParams({
        page: String(payload?.page || 1),
        page_size: String(payload?.page_size || 10),
    })
    if (payload?.q) params.append('q', payload.q)
    if (payload?.filters) params.append('filters', payload.filters)
    if (payload?.status) params.append('status', payload.status)
    if (payload?.schedule_id)
        params.append('schedule_id', String(payload.schedule_id))
    if (payload?.start_date) params.append('start_date', payload.start_date)
    if (payload?.end_date) params.append('end_date', payload.end_date)

    const response = await datacentral.get<ResponseArray<OncallSwapResponse>>(
        `/oncall/swaps?${params.toString()}`
    )

    // Transform result to results for GokuDataGrid compatibility
    const data = response.data as any
    if (data.result && !data.results) {
        data.results = data.result
    }
    return data
}

export const getMyPendingSwaps = async () => {
    const response = await datacentral.get<ResponseArray<OncallSwapResponse>>(
        '/oncall/swaps/pending'
    )
    return response.data
}

export const getAllPendingSwaps = async () => {
    const response = await datacentral.get<ResponseArray<OncallSwapResponse>>(
        '/oncall/swaps/pending/all'
    )
    return response.data
}

export const getMySwaps = async (payload?: MySwapsFilters) => {
    const params = new URLSearchParams({
        start_date: payload?.start_date || '',
        end_date: payload?.end_date || '',
    })

    const response = await datacentral.get<ResponseArray<OncallSwapResponse>>(
        `/oncall/swaps/my?${params.toString()}`
    )

    // Transform result to results for compatibility
    const data = response.data as any
    if (data.result && !data.results) {
        data.results = data.result
    }
    return data
}

export const getOncallSwapByID = async (
    id: number
): Promise<ResponseObject<OncallSwapResponse>> => {
    const response = await datacentral.get<ResponseObject<OncallSwapResponse>>(
        `/oncall/swaps/${id}`
    )
    return response.data
}

export const createOncallSwap = async (
    payload: CreateSwapPayload
): Promise<ResponseObject<OncallSwapResponse> | ErrorResponse> => {
    const response = await datacentral.post('/oncall/swaps', payload)
    return response.data
}

export const acceptOncallSwap = async (payload: {
    id: number
    data: SwapFeedbackPayload
}): Promise<ResponseObject<OncallSwapResponse> | ErrorResponse> => {
    const response = await datacentral.put(
        `/oncall/swaps/${payload.id}/accept`,
        payload.data
    )
    return response.data
}

export const rejectOncallSwap = async (payload: {
    id: number
    data: SwapFeedbackPayload
}): Promise<ResponseObject<OncallSwapResponse> | ErrorResponse> => {
    const response = await datacentral.put(
        `/oncall/swaps/${payload.id}/reject`,
        payload.data
    )
    return response.data
}

export const approveOncallSwap = async (payload: {
    id: number
    data: SwapFeedbackPayload
}): Promise<ResponseObject<OncallSwapResponse> | ErrorResponse> => {
    const response = await datacentral.put(
        `/oncall/swaps/${payload.id}/approve`,
        payload.data
    )
    return response.data
}

export const cancelOncallSwap = async (
    id: number
): Promise<ResponseObject<OncallSwapResponse> | ErrorResponse> => {
    const response = await datacentral.put(`/oncall/swaps/${id}/cancel`)
    return response.data
}
